import * as React from 'react';

import {Box, Stack, Typography} from '@mui/material';
import {AggregationSurveySentimentSeries} from 'crqlar-react-common/dist/models/aggregation/surveyAggregation.model';
import {useTranslation} from 'react-i18next';

import {CardHeadline} from '../../../insights/kit/Headline';
import {SeriesCard} from '../../../insights/kit/SeriesCard';
import {useInsightsFormatters} from "../../../insights/kit/insightsFormat";
import {InsightsLoading} from '../../../insights/kit/insightsLoading';
import {InsightsColumn, InsightsTable} from '../../../insights/kit/InsightsTable';
import {
    lowerIsBetterFor,
    SENTIMENT_COLORS,
    SentimentChart,
    SentimentKey,
    SentimentLabels,
    SentimentSlice,
    SentimentSpec,
} from '../charts/SentimentChart';

interface SentimentCardProps {
    series?: AggregationSurveySentimentSeries;
    loading?: InsightsLoading;
}

interface SentimentPoint {
    positive: number;
    neutral: number;
    negative: number;
}

const NOTHING: SentimentPoint = {positive: 0, neutral: 0, negative: 0};

const totalOf = (point: SentimentPoint) => point.positive + point.neutral + point.negative;

function toSentimentSlices(
    data: SentimentPoint,
    compareData: SentimentPoint | undefined,
    labels: Pick<SentimentLabels, SentimentKey>
): SentimentSlice[] {
    const total = totalOf(data);
    const compareTotal = compareData == null ? 0 : totalOf(compareData);

    const share = (value: number, base: number) => (base === 0 ? 0 : (value / base) * 100);

    return (['positive', 'neutral', 'negative'] as SentimentKey[]).map(key => {
        const count = data[key];
        const compareCount = compareData?.[key] ?? null;

        const currentShare = share(count, total);
        const compareShare = compareCount == null ? null : share(compareCount, compareTotal);

        return {
            key,
            label: labels[key],
            color: SENTIMENT_COLORS[key],
            count,
            share: currentShare,
            compareCount,
            compareShare,
            delta: compareShare == null ? null : currentShare - compareShare,
        };
    });
}

interface SliceTableProps {
    slices: SentimentSlice[];
    labels: SentimentLabels & {sentiment: string};
    hasCompare: boolean;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
    formatPoints: (value: number) => string;
}

function SliceTable({slices, labels, hasCompare, formatCount, formatPercent, formatPoints}: SliceTableProps) {
    const columns: Array<InsightsColumn<SentimentSlice> | false> = [
        {
            key: 'sentiment',
            kind: 'label',
            label: labels.sentiment,
            cell: slice => (
                <Stack direction={'row'} alignItems={'center'} spacing={1}>
                    <Box sx={{width: 10, height: 10, borderRadius: '2px', bgcolor: slice.color, flexShrink: 0}}/>
                    <Box component={'span'}>{slice.label}</Box>
                </Stack>
            ),
        },
        {key: 'count', kind: 'value', label: labels.count, cell: slice => slice.count, format: formatCount},
        {key: 'share', kind: 'detail', label: labels.share, cell: slice => slice.share, format: formatPercent},
        hasCompare && {key: 'compare', kind: 'compare', label: labels.compare, cell: slice => slice.compareCount, format: formatCount},
        hasCompare && {
            key: 'delta',
            kind: 'delta',
            label: labels.delta,
            cell: slice => slice.delta,
            format: formatPoints,
            lowerIsBetter: lowerIsBetterFor,
            color: slice => (slice.key === 'neutral' ? 'text.secondary' : undefined),
            comparison: true,
        },
    ];

    return <InsightsTable label={labels.sentiment} columns={columns} rows={slices} rowKey={slice => slice.key}/>;
}

export function SentimentCard({series, loading}: SentimentCardProps) {
    const {t} = useTranslation();

    const data = series?.current ?? NOTHING;
    const compareData = series?.compare ?? undefined;
    const hasCompare = compareData != null;

    const {count: formatCount, percent: formatPercent, points: formatPoints} = useInsightsFormatters();

    const labels = React.useMemo<SentimentLabels>(
        () => ({
            positive: t('Positive'),
            neutral: t('Neutral'),
            negative: t('Negative'),
            replies: t('replies'),
            count: t('Replies'),
            compare: t('Compare period'),
            share: t('Share'),
            delta: t('Change'),
        }),
        [t],
    );

    const tableLabels = React.useMemo(() => ({sentiment: t('Sentiment'), ...labels}), [labels, t]);

    const slices = React.useMemo(() => toSentimentSlices(data, compareData, labels), [compareData, data, labels]);

    const positiveShare = series?.summary.positiveShare ?? null;
    const previousPositiveShare = series?.summary.previousPositiveShare ?? null;
    const positiveDelta = positiveShare == null || previousPositiveShare == null ? null : positiveShare - previousPositiveShare;

    const classified = totalOf(data);

    const headline = (
        // "0 positive" off three comments is not a verdict on the survey: the base has to be visible.
        <CardHeadline
            value={positiveShare == null ? '—' : formatPoints(positiveShare)}
            suffix={t('From free-text answer count', {count: classified})}
            change={previousPositiveShare == null ? null : {
                title: t('Positive share'),
                current: positiveShare == null ? '—' : formatPoints(positiveShare),
                previous: formatPoints(previousPositiveShare),
                delta: positiveDelta,
                formatDelta: formatPoints,
            }}
        />
    );

    const spec: SentimentSpec = {slices, labels, formatCount, formatPercent, formatPoints};

    return (
        <SeriesCard
            title={t('Sentiment')}
            hint={t('Every reply is read as positive, neutral or negative: negative as soon as one answer is, positive when one is and none is negative. The headline is the share that read positively; the outer ring is this period and the inner ring the one it is compared against.')}
            headline={headline}
            loading={loading}
            empty={classified === 0 && !hasCompare ? (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t('No responses in this period.')}
                </Typography>
            ) : undefined}
            chart={<SentimentChart {...spec}/>}
            table={(
                <SliceTable
                    slices={slices}
                    labels={tableLabels}
                    hasCompare={hasCompare}
                    formatCount={formatCount}
                    formatPercent={formatPercent}
                    formatPoints={formatPoints}
                />
            )}
        />
    );
}
